import React, { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import HeaderPage from "../../components/HeaderPage";
import {
  useGetTrackQuery,
  useBulkSubmitQuestionsMutation,
} from "../../services/users/learningTrack";
import LoadingIndicator from "../../components/LoadingIndicator";
import ChoiceQuestion from "../../components/cards/Exam/ChoiceQuestion";
import ExamProgressFloat from "../../components/cards/Exam/ExamProgressFloat";
import ExamTimerFloat from "../../components/cards/Exam/ExamTimerFloat";
import NotFoundPage from "../404";
import PassedLearningPathExamModal from "../../components/Modals/PassedLearningPathExamModal";
import FailedLearningPathExamModal from "../../components/Modals/FailedLearningPathExamModal";

const PASSED_PATH_EXAM_MODAL_ID = "passed-path-exam-modal";
const FAILED_PATH_EXAM_MODAL_ID = "failed-path-exam-modal";

const formatTimeRemaining = (totalSeconds) => {
  if (totalSeconds == null || totalSeconds < 0) return null;
  const m = Math.floor(totalSeconds / 60);
  const s = Math.max(0, totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// Normalize API question to { id, title, answers: [{ id, title }] }. Ignore course, lesson, time, archived for display.
const normalizeQuestions = (rawQuestions) => {
  if (!Array.isArray(rawQuestions)) return [];
  return rawQuestions.map((q) => ({
    id: q.id,
    title: q.title ?? q.question ?? "",
    answers: (q.answers ?? []).map((a) => ({
      id: a.id,
      title: a.title ?? a.answer ?? "",
    })),
  }));
};

const LearningPathExamPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation(["learningPaths", "common"]);

  const stateQuestions = location.state?.questions;
  const statePathName = location.state?.learningPathName;

  const { data: learningTrack, isLoading } = useGetTrackQuery(id, {
    skip: !!stateQuestions?.length,
  });

  const trackData = stateQuestions?.length
    ? { title: statePathName, questions: stateQuestions }
    : learningTrack?.data;

  const questions = useMemo(
    () => normalizeQuestions(trackData?.questions),
    [trackData?.questions],
  );

  const pathTitle = trackData?.title ?? "";

  const examTimeMinutes = trackData?.exam_time;
  const hasTimer =
    examTimeMinutes != null &&
    examTimeMinutes !== "" &&
    !Number.isNaN(Number(examTimeMinutes)) &&
    Number(examTimeMinutes) > 0;
  const initialSeconds = hasTimer
    ? Math.floor(Number(examTimeMinutes) * 60)
    : null;

  const [isExamStarted, setIsExamStarted] = useState(false);
  const [myAnswers, setMyAnswers] = useState([]);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(null);
  const [examResult, setExamResult] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [showPassedModal, setShowPassedModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);

  const submittedRef = useRef(false);
  const myAnswersRef = useRef(myAnswers);
  myAnswersRef.current = myAnswers;

  const timeExpired =
    timeRemainingSeconds !== null && timeRemainingSeconds <= 0;

  const [bulkSubmitQuestions, { isLoading: isSubmitting }] =
    useBulkSubmitQuestionsMutation();

  const handleChangingAnswer = (questionId, answersWithId) => (e) => {
    const selectedTitle = e.target.value;
    const chosen = answersWithId.find((a) => a.title === selectedTitle);
    setMyAnswers((prev) =>
      prev
        .filter((a) => a.id !== questionId)
        .concat(chosen ? [{ id: questionId, answer: chosen.id }] : []),
    );
  };

  const handleFinishExam = async (forceSubmit = false) => {
    if (submittedRef.current) return;

    const answersToSubmit = myAnswersRef.current;
    const answeredCount = answersToSubmit.length;
    const totalCount = questions.length;
    if (!forceSubmit && answeredCount !== totalCount) return;

    submittedRef.current = true;

    const answers = answersToSubmit.map((a) => ({
      question_id: a.id,
      selected_answer_id: a.answer,
    }));

    try {
      const result = await bulkSubmitQuestions({
        learningTrackId: id,
        answers,
      }).unwrap();

      // Parse response - data is nested under result.data
      const summary = result?.data?.summary;
      const enrollment = result?.data?.enrollment;

      if (summary) {
        setExamResult(summary);
      }
      if (enrollment) {
        setEnrollmentData(enrollment);
      }

      // Show appropriate modal based on passed status
      if (summary?.passed) {
        setShowPassedModal(true);
      } else {
        setShowFailedModal(true);
      }
    } catch (err) {
      submittedRef.current = false;
      toast.error(
        err?.data?.message || err?.message || "Failed to submit exam.",
      );
    }
  };

  // Start timer when exam starts
  const handleStartExam = () => {
    setIsExamStarted(true);
    if (hasTimer) {
      setTimeRemainingSeconds(Math.floor(Number(examTimeMinutes) * 60));
    }
  };

  // Countdown and auto-submit when time reaches 0
  useEffect(() => {
    if (
      !isExamStarted ||
      !hasTimer ||
      timeRemainingSeconds == null ||
      timeRemainingSeconds <= 0
    )
      return;
    const intervalId = setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          handleFinishExam(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isExamStarted, hasTimer]);

  // Modal control effects
  useEffect(() => {
    const el = document.getElementById(PASSED_PATH_EXAM_MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (showPassedModal) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [showPassedModal]);

  useEffect(() => {
    const el = document.getElementById(FAILED_PATH_EXAM_MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (showFailedModal) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [showFailedModal]);

  if (isLoading && !stateQuestions?.length) return <LoadingIndicator />;
  if (!trackData || !questions?.length) return <NotFoundPage />;

  const listItems = [
    { name: t("learningTracks"), href: "/employee/learning_paths" },
    { name: pathTitle, href: `/employee/learning_paths/${id}` },
    { name: t("pathExamTitle"), href: "#" },
  ];

  return (
    <>
      <HeaderPage
        sectionTitle={t("pathExamTitle")}
        listItems={listItems}
        progressBarFullDetails=""
        progressBarPercentageText=""
        progressBarSubDetails=""
      />
      <div className="container-fluid">
        <div className="row justify-content-center p-4">
          <div className="col-lg-10 col-md-12">
            {!isExamStarted ? (
              <div className="card-style card-course-exam">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h4 className="font_24 font-extrabold mb-0">
                    {t("pathExamTitle")} (
                    {t("pathExamQuestionCount", {
                      count: questions.length,
                    })}
                    )
                  </h4>
                  <button
                    type="button"
                    className="btn btn-main-color card-course-exam__action"
                    onClick={handleStartExam}
                  >
                    {t("startPathExam")}
                    <span className="fas fa-arrow-right fa-fw ms-2" />
                  </button>
                </div>

                <div className="card-course-exam__list mt-2">
                  <h6 className="font_16 font-extrabold font-color-grey card-course-exam__title">
                    {t("pathExamRulesTitle")}
                  </h6>
                  <ul>
                    <li className="font_14 font-color-grey card-course-exam__list__item d-flex align-items-start">
                      <span
                        className="fas fa-info-circle font-color-grey me-2 mt-1"
                        style={{ fontSize: "10px" }}
                        aria-hidden
                      />
                      <span>{t("pathExamRuleCompleteInTime")}</span>
                    </li>
                    <li className="font_14 font-color-grey card-course-exam__list__item d-flex align-items-start">
                      <span
                        className="fas fa-info-circle font-color-grey me-2 mt-1"
                        style={{ fontSize: "10px" }}
                        aria-hidden
                      />
                      <span>{t("pathExamRuleAutoClose")}</span>
                    </li>
                    <li className="font_14 font-color-grey card-course-exam__list__item d-flex align-items-start">
                      <span
                        className="fas fa-info-circle font-color-grey me-2 mt-1"
                        style={{ fontSize: "10px" }}
                        aria-hidden
                      />
                      <span>{t("pathExamRuleOneAttempt")}</span>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="content-course-exam">
                <ExamProgressFloat
                  answeredQuestionNumber={myAnswers.length}
                  totalQuestionNumber={questions.length}
                />
                <ExamTimerFloat
                  countdownFormatted={
                    timeRemainingSeconds != null
                      ? formatTimeRemaining(timeRemainingSeconds)
                      : null
                  }
                />
                <p className="font-color-grey font_20 mb-4">
                  <span className="font-extrabold">{questions.length}</span>{" "}
                  {t("question")}
                </p>
                {questions.map((question, i) => (
                  <div key={question.id} className="card-style mb-4">
                    <h1 className="font_16 font-color-grey">
                      {t("questionNumberLabel", { number: i + 1 })}
                    </h1>
                    <ChoiceQuestion
                      raidoName={question.id}
                      title={question.title}
                      answers={question.answers.map((a) => a.title)}
                      handleChangingAnswer={handleChangingAnswer(
                        question.id,
                        question.answers,
                      )}
                    />
                  </div>
                ))}
                <div className="card-style mb-4">
                  <h1 className="font_20 font-color-grey mb-4">
                    {t("pathExamFinishedConfirm")}
                  </h1>
                  <button
                    type="button"
                    className="btn btn-main-color font-15 me-3"
                    style={{ maxWidth: "240px" }}
                    onClick={() => handleFinishExam(false)}
                    disabled={
                      timeExpired ||
                      myAnswers.length !== questions.length ||
                      isSubmitting
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {t("common:Loading")}
                      </>
                    ) : timeExpired ? (
                      t("learningPaths:pathExamTimeEnded")
                    ) : (
                      t("finishPathExam")
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PassedLearningPathExamModal
        id={PASSED_PATH_EXAM_MODAL_ID}
        isOpened={showPassedModal}
        totalQuestions={examResult?.total_questions ?? 0}
        correctCount={examResult?.correct_count ?? 0}
        incorrectCount={examResult?.incorrect_count ?? 0}
        percentage={examResult?.percentage}
        learningPathId={id}
      />
      <FailedLearningPathExamModal
        id={FAILED_PATH_EXAM_MODAL_ID}
        isOpened={showFailedModal}
        totalQuestions={examResult?.total_questions ?? 0}
        correctCount={examResult?.correct_count ?? 0}
        incorrectCount={examResult?.incorrect_count ?? 0}
        percentage={examResult?.percentage}
        attempts={enrollmentData?.attempts}
        nextAttemptTime={enrollmentData?.next_attempt_time}
        learningPathId={id}
      />
    </>
  );
};

export default LearningPathExamPage;
